import React from 'react';
import { string, func, bool, oneOfType } from 'prop-types';
import classNames from 'classnames';
import moment from 'moment';
import 'moment/locale/es';

import { useConfiguration } from '../../context/configurationContext';

import { FormattedMessage, intlShape, injectIntl } from '../../util/reactIntl';
import { displayPrice } from '../../util/configHelpers';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { propTypes } from '../../util/types';
import { formatMoney } from '../../util/currency';
import { ensureListing } from '../../util/data';
import { createSlug } from '../../util/urlHelpers';
import { isBookingProcessAlias } from '../../transactions/transaction';

import { AspectRatioWrapper, Avatar, NamedLink, ResponsiveImage } from '../../components';

import css from './ListingRequestCard.module.css';
import { getServiceDetails, getServices } from '../../config/requests';

const MIN_LENGTH_FOR_LONG_WORDS = 10;

const priceData = (price, currency, intl) => {
  if (price && price.currency === currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: intl.formatMessage(
        { id: 'ListingRequestCard.unsupportedPrice' },
        { currency: price.currency }
      ),
      priceTitle: intl.formatMessage(
        { id: 'ListingRequestCard.unsupportedPriceTitle' },
        { currency: price.currency }
      ),
    };
  }
  return {};
};

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { loadAfterInitialRendering: 3000 });

// util: primer string no vacío y distinto a "recruitment"
const pickFirst = (...vals) =>
  vals.find(v => typeof v === 'string' && v.trim() && v.trim().toLowerCase() !== 'recruitment') || '';

export const ListingCardComponent = props => {
  const config = useConfiguration();
  const {
    className,
    rootClassName,
    intl,
    listing,
    renderSizes,
    setActiveListing,
    showAuthorInfo,
    sendQueryParams,
    isSuggestion,
    request,
    isManageMode = false,
    manageActions,
    titleOverride,
  } = props;

  const SERVICES = getServices(intl) || [];
  const currentListing = ensureListing(listing);
  const id = currentListing.id.uuid;

  const a = currentListing.attributes || {};
  const { title: rawTitleProp = '', publicData = {}, state } = a;

  const companyName = listing.author?.attributes?.profile?.publicData?.businessName || '';
  const description = publicData?.description || '';
  const location = publicData?.requestLocation?.selectedPlace?.address || '';
  const salary = publicData?.budget || '';
  const { listingType } = publicData || {};

  // Moneda / tarifa
  const currency = listing?.attributes?.price?.currency || publicData?.currency || 'USD';
  const priceAmount = listing?.attributes?.price?.amount;
  const priceCurrency = listing?.attributes?.price?.currency || currency;
  const hourlyRate =
    publicData?.hourlySalaryRange ||
    publicData?.fee ||
    (priceAmount ? priceAmount / 100 : null);

  const classes = classNames(rootClassName || css.root, className, css.card);

  // Total horas tasks
  const tasksTotalHours = publicData?.tasks?.reduce((acc, task) => {
    const splitTask = task.split('-');
    const hours = splitTask[0];
    const totalHours = acc + parseInt(hours);
    return totalHours;
  }, 0);

  const date = new Date(a?.createdAt);
  const formattedDate = moment(date).locale('es').format('D [de] MMMM [de] YYYY');

  // ===== TÍTULO calculado (si "Borrador" o vacío, siempre sustituimos) =====
  const override = typeof titleOverride === 'string' && titleOverride.trim() ? titleOverride.trim() : null;

  let finalTitle;
  if (override) {
    finalTitle = override;
  } else {
    const serviceLabel = SERVICES.find(s => s.option === publicData?.service)?.label_card;
    const isRecruitment = publicData?.service === 'recruitment';

    const vacancyFromPD = pickFirst(
      publicData?.vacancyTitle,
      publicData?.vacancyName,
      publicData?.jobTitle,
      publicData?.positionTitle,
      publicData?.positionName,
      publicData?.roleTitle,
      publicData?.roleName,
      publicData?.jobName,
      publicData?.nombreVacante,
      publicData?.puesto,
      publicData?.name,
      publicData?.title
    );

    const listingFields = (config?.listing?.listingFields || []);
    const roleFieldConfig = listingFields.find(f => f.key === 'role');
    const roleOptions = roleFieldConfig?.enumOptions || [];
    const roleLabel = roleOptions.find(o => o.option === publicData?.role)?.label || null;

    const genericBackup = pickFirst(
      publicData?.title,
      publicData?.name,
      publicData?.nombrePerfil,
      publicData?.service,
      roleLabel,
      serviceLabel,
      companyName
    );

    const rawTitle = (rawTitleProp || '').trim();
    const looksLikeDraftWord = /^(borrador|draft)$/i.test(rawTitle);
    const typeDefault =
      listingType === 'request'
        ? 'Solicitud'
        : listingType === 'salesforce'
        ? 'Perfil sin título'
        : 'Publicación';

    const preferredForRecruitment = vacancyFromPD || genericBackup || typeDefault;
    const preferredForOthers = genericBackup || typeDefault;

    finalTitle = isRecruitment
      ? ((looksLikeDraftWord || !rawTitle) ? preferredForRecruitment : rawTitle)
      : ((looksLikeDraftWord || !rawTitle) ? preferredForOthers : rawTitle);
  }

// === DEBUG solo en /s ===
const loggedOnceRef = React.useRef(false);
React.useEffect(() => {
  if (typeof window === 'undefined') return;
  if (!window.location.pathname.startsWith('/s')) return;
  if (loggedOnceRef.current) return;
  loggedOnceRef.current = true;

  try {
    console.groupCollapsed(
      `%c[S] ListingRequestCard`,
      'color:#5e17eb;font-weight:bold',
      `id: ${id}`,
      `title: "${finalTitle}"`
    );
    console.log('attributes.title (rawTitleProp):', rawTitleProp);
    console.log('publicData:', publicData);
    console.log('companyName:', companyName);
    console.log('listingType:', listingType);
    console.groupEnd();
  } catch (e) {
    console.warn('Debug ListingRequestCard error:', e);
  }
}, [id, finalTitle, rawTitleProp, publicData, companyName, listingType]);


  
  const slug = createSlug(finalTitle || 'listing');

  // === role para el CSS temático ===
  const roleAttr = String(
    currentListing?.attributes?.publicData?.role ||
    listing?.author?.attributes?.profile?.publicData?.role ||
    listing?.author?.attributes?.profile?.publicData?.userType ||
    ''
  ).toLowerCase().includes('consult')
    ? 'role-consultant'
    : 'role-company';

  return (
    <div className={classes} data-rolecard={roleAttr}>
      <div className={css.cardHeader}>
        <div className={css.company}>
          <span className={css.icon}>◈</span>
          {listing.author?.attributes?.profile?.publicData?.businessName || ''}
        </div>
      </div>
      <div className={css.cardBody}>
        <div className={css.title}>{finalTitle}</div>

        {currentListing.attributes.description && (
          <div style={{ color: '#4a5568', marginBottom: 12, fontSize: '0.97rem', background: '#f8fafc', padding: 10, borderRadius: 6 }}>
            {currentListing.attributes.description}
          </div>
        )}
        {description && <div className={css.description}>{description}</div>}

        <div className={css.details}>
          {tasksTotalHours && (
            <div className={css.cell}>
              <span className={css.icon}>◈</span>
              <span className={css.tasksTotalHours}><b>No. de horas: </b>{tasksTotalHours} horas</span>
            </div>
          )}
          {publicData?.modality && (
            <div className={css.cell}>
              <span className={css.icon}>◈</span>
              <span className={css.modality}><b>Modalidad de trabajo: </b>{publicData?.modality === 'in-person' ? 'Presencial' : publicData?.modality === 'hybrid' ? 'Híbrido' : 'Remoto'}</span>
            </div>
          )}
          {publicData?.typeOfContract && (
            <div className={css.cell}>
              <span className={css.icon}>◈</span>
              <span className={css.typeOfContract}><b>Tipo de contrato: </b>{publicData?.typeOfContract === 'full-time' ? 'Tiempo completo' : publicData?.typeOfContract === 'part-time' ? 'Medio tiempo' : 'Independiente'}</span>
            </div>
          )}
          {location && (
            <div className={css.cell}>
              <span className={css.icon}>◈</span>
              <span className={css.location}><b>Ubicación: </b>{location}</span>
            </div>
          )}
          {salary && (
            <div className={css.cellCol}>
              <span className={css.icon}>◈</span>
              <span className={css.salary}><b>Presupuesto total: </b></span>
              <span className={css.salary}>{salary ? `$${salary}` : ''}</span>
            </div>
          )}
          {publicData?.salaryRange && (
            <div className={css.cellCol}>
              <span className={css.icon}>◈</span>
              <span className={css.salary}><b>Rango de Salario Mensual: </b></span>
              <span className={css.salary}>{publicData?.salaryRange ? `$${publicData?.salaryRange}` : ''}</span>
            </div>
          )}
          {/* Tarifa sólo si no es recruitment */}
          {publicData?.service !== 'recruitment' && hourlyRate && (
            <div className={css.cellCol}>
              <span className={css.icon}>◈</span>
              <span className={css.salary}><b>Tarifa: </b></span>
              <span className={css.salary}>
                <span className={css.salaryCurrency}>{priceCurrency}</span>
                <span className={css.salaryAmount}>{`$${hourlyRate}`}</span>
              </span>
            </div>
          )}
          {formattedDate && (
            <div className={css.cell}>
              <span className={css.icon}>◈</span>
              <span className={css.modality}><b>Fecha: </b>{formattedDate}</span>
            </div>
          )}
        </div>
      </div>

      {!isManageMode && (
        <div className={css.cardFooter}>
          <NamedLink className={css.applyBtn} name="ListingPage" params={{ id, slug }}>
            Aplica
          </NamedLink>
        </div>
      )}
      {isManageMode && manageActions}
    </div>
  );
};

ListingCardComponent.defaultProps = {
  className: null,
  rootClassName: null,
  renderSizes: null,
  setActiveListing: null,
  showAuthorInfo: true,
  isSuggestion: false,
};

ListingCardComponent.propTypes = {
  className: string,
  rootClassName: string,
  intl: intlShape.isRequired,
  listing: oneOfType([propTypes.listing, propTypes.ownListing]).isRequired,
  showAuthorInfo: bool,
  isSuggestion: bool,
  renderSizes: string,
  setActiveListing: func,
  titleOverride: string,
};

export default injectIntl(ListingCardComponent);
